import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import "../parcel-tracking-modals.scss";
import { Row } from "antd";
import {
  AlertBox,
  Button,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";

interface AssignDriverBoxesModalProps {
  onSubmit?: any;
  boxInfo?: any;
  vehicleOptions?: any;
  isFromBatch?: boolean
  handleCancel: any;
}

const AssignDriverBoxesModal: React.FC<AssignDriverBoxesModalProps> = ({
  onSubmit,
  boxInfo: info,
  vehicleOptions,
  handleCancel,
  isFromBatch = false
}) => {
  const { t } = useTranslation();

  const initialRole =
    info?.assignedDriver === "THIRD_PARTY" ? "THIRDPARTY" : "COMPANY";

  const vehicleOptionsThirdParty = [
    {
      _id: "vehicle_types/0f00aefe-d51d-4a0e-805d-dd05e18de9ae",
      name: "Car",
    },
    {
      _id: "vehicle_types/8c36efff-b0b5-424a-9c1e-cde41ea6fac5",
      name: "Van",
    },
    {
      _id: "vehicle_types/739e81f7-73ba-401a-9823-490de6f93f98",
      name: "Bike",
    },
  ];

  const findVehicle = (profileID) => {
    for (let i = 0; i < vehicleOptions?.length; i++) {
      const vehicle = vehicleOptions[i];

      if (vehicle?.driver[0]?.profileID === profileID) {
        return vehicle?._id;
      }
    }
  };

  const driverFullName =
    info?.assignedDriver?.fullName ?? info?.assignedDriverData?.fullName;

  const initialInput = {
    batchParcelID: "",
    boxID: [],
    companyType: initialRole,
    vehicleID:
      initialRole === "COMPANY"
        ? findVehicle(info?.assignedDriver?._id)
        : "",
    vehicleType: info?.assignedDriverData?.vehicleData?.vehicleType ?? "",
    thirdPartyCompany:
      initialRole === "THIRDPARTY" ? info?.assignedDriverData?.fullName : "",
    driverName: initialRole === "COMPANY" ? driverFullName : "",
    driverID: initialRole === "COMPANY" ? info?.assignedDriver?._id : "",
    sortingHub: info?.midMileInfo?.sortingHub ?? "",
  };

  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);

  useEffect(() => {
    if (info.length) {
      let boxIDs: any = [];
      for (let i = 0; i < info.length; i++) {
        boxIDs.push(info[i]._id);
      }
      setInput({ ...input, boxID: boxIDs });
    } else {
      let boxIDValue = input.boxID;
      boxIDValue.push(info._id);
      setInput({ ...input, boxID: boxIDValue });
    }
  }, [info]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });

    let tempInput = { ...input, [name]: value };

    if (name === "vehicleID") {
      // autopopulate the driver id details
      const hasDriver = vehicleOptions.find((v) => v._id === value)?.driver;

      if (hasDriver) {
        tempInput = {
          ...tempInput,
          driverID: hasDriver?.profileID,
          driverName: hasDriver?.fullName,
        };
      }
    }

    setInput(tempInput);
  };

  const handleSubmit = () => {
    try {
      let valInput: any = {};

      const isRoleThirdParty = input.companyType == "THIRDPARTY" ? true : false;

      valInput = {
        boxParcelIDs: input.boxID,
        assignedDriver: isRoleThirdParty ? "THIRD_PARTY" : input.driverID, // if third party, this is COMPANY_TYPE.THIRD_PARTY otherwise the assignedDriver._id
        assignedDriverData: isRoleThirdParty // if third party, this is filled
          ? {
              fullName: input.thirdPartyCompany,
              vehicleData: {
                vehicleType: input.vehicleType,
              },
            }
          : null,
      };

      if (info._id.includes('batch')) {
        valInput = {
          ...valInput,
          boxParcelIDs: null,
          batchParcelID: info._id
        }
      }

      onSubmit(valInput);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  return (
    <div className="parcel-tracking-modal">
      <ModuleTitleBar titleText={t("title.assignDriver").toUpperCase()} />
      <Row>
        <h3>{info?.length} Boxes</h3>
      </Row>
      <div className="mt-20">
        <SelectInput
          showSearch
          label={t("label.selectCompanyType")}
          options={[
            { label: "DIOLKO", value: "COMPANY" },
            { label: "3rd Party", value: "THIRDPARTY" },
          ]}
          isRequired
          value={input.companyType}
          name="companyType"
          placeholder={t("txt.selectHere")}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          onChange={(value: string | string[]) =>
            handleSelectChange(value, "companyType")
          }
        />
      </div>
      <div>
        <div className="mt-10">
          <SelectInput
            showSearch
            label={t("label.selectVehicleType")}
            options={
              input.companyType === "COMPANY"
                ? vehicleOptions?.map((vehicle: any) => ({
                    label: vehicle.name,
                    value: vehicle._id,
                  }))
                : vehicleOptionsThirdParty?.map((vehicle) => ({
                    label: vehicle.name,
                    value: vehicle._id,
                  }))
            }
            filterOption={(inputValue, option) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
            isRequired
            value={input.companyType == "COMPANY" ? input.vehicleID : input.vehicleType}
            name="vehicleType"
            placeholder={t("txt.selectHere")}
            onChange={(value: string | string[]) =>
              handleSelectChange(value, input.companyType === "COMPANY" ? "vehicleID" : "vehicleType")
            }
          />
        </div>
        <div className="mt-10">
          {input.companyType === "COMPANY" ? (
            <TextInput
              value={input.driverName}
              isReadonly={true}
              label={t("title.assignDriver")}
            />
          ) : (
            <TextInput
              isRequired={true}
              label={t("label.thirdPartyName")}
              placeholder={t("txt.thirdPartyName")}
              value={input.thirdPartyCompany}
              name="thirdPartyCompany"
              onChange={handleFieldChange}
            />
          )}
        </div>
      </div>
      <div className="mt-30 right">
        <Button
          type={"primary"}
          label={t("action.confirm")}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={handleCancel}
        />
      </div>
    </div>
  );
};

export default AssignDriverBoxesModal;
